import { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, Users, Eye, UserCog, ShieldCheck, Globe, Video } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DoctorsAdmin } from "@/components/medicos/DoctorsAdmin";
import { SecretariesAdmin } from "@/components/medicos/SecretariesAdmin";
import { HealthPlansAdmin } from "@/components/medicos/HealthPlansAdmin";
import { DoctorPortal } from "@/components/medicos/DoctorPortal";
import { ClinicPublicAdmin } from "@/components/medicos/ClinicPublicAdmin";
import { Telemedicina } from "@/components/medico/Telemedicina";
import { INITIAL_DOCTORS } from "@/data/cfmData";

export default function Medicos() {
  const [doctors, setDoctors] = useState(INITIAL_DOCTORS);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 shadow-[0_4px_12px_rgba(37,99,235,0.35)]">
              <Activity className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
                VeloClin
              </span>
              <span className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                Corpo Clínico
              </span>
            </div>
          </div>
          <Link
            to="/agenda"
            data-testid="back-to-agenda-med"
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Agenda
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-6 md:px-8 md:py-8">
        <div className="mb-6 flex flex-col gap-1">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Gestão do Corpo Clínico
          </h1>
          <p className="text-sm text-slate-500">
            Cadastre médicos com especialidade CFM e repasse individual, e acompanhe a transparência
            dos ganhos.
          </p>
        </div>

        <Tabs defaultValue="admin" className="w-full">
          <TabsList className="mb-6 grid w-full grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1 sm:inline-grid sm:w-auto sm:grid-cols-6">
            <TabsTrigger
              value="admin"
              data-testid="tab-admin"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <Users className="h-4 w-4" />
              Médicos
            </TabsTrigger>
            <TabsTrigger
              value="secretarias"
              data-testid="tab-secretarias"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <UserCog className="h-4 w-4" />
              Secretárias
            </TabsTrigger>
            <TabsTrigger
              value="convenios"
              data-testid="tab-convenios"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <ShieldCheck className="h-4 w-4" />
              Convênios
            </TabsTrigger>
            <TabsTrigger
              value="portal"
              data-testid="tab-portal"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <Eye className="h-4 w-4" />
              Portal do Médico
            </TabsTrigger>
            <TabsTrigger
              value="pagina"
              data-testid="tab-pagina"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <Globe className="h-4 w-4" />
              Página Pública
            </TabsTrigger>
            <TabsTrigger
              value="telemedicina"
              data-testid="tab-telemedicina"
              className="gap-1.5 rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm sm:text-sm"
            >
              <Video className="h-4 w-4" />
              Telemedicina
            </TabsTrigger>
          </TabsList>

          <TabsContent value="admin" className="mt-0 focus-visible:outline-none">
            <DoctorsAdmin doctors={doctors} setDoctors={setDoctors} />
          </TabsContent>
          <TabsContent value="secretarias" className="mt-0 focus-visible:outline-none">
            <SecretariesAdmin />
          </TabsContent>
          <TabsContent value="convenios" className="mt-0 focus-visible:outline-none">
            <HealthPlansAdmin />
          </TabsContent>
          <TabsContent value="portal" className="mt-0 focus-visible:outline-none">
            <DoctorPortal doctors={doctors} />
          </TabsContent>
          <TabsContent value="pagina" className="mt-0 focus-visible:outline-none">
            <ClinicPublicAdmin />
          </TabsContent>
          <TabsContent value="telemedicina" className="mt-0 focus-visible:outline-none">
            <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50/60 p-3 text-xs text-teal-800">
              <b>Telemedicina liberada</b> para os planos Basic e Max. Gere o link e atenda seus pacientes por vídeo com prontuário e prescrição integrados.
            </div>
            <Telemedicina />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
